import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, FilterList, ExpandMore } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { questionAPI, courseAPI } from '../../../services/api';
import ConfirmDialog from '../../../components/ConfirmDialog';

const QuestionList = () => {
  const [questions, setQuestions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Filter states
  const [filterCourse, setFilterCourse] = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [filterSubject, setFilterSubject] = useState('');

  // Form states
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);

  // Pagination state - one page state per subject
  const [subjectPages, setSubjectPages] = useState({});
  const questionsPerPage = 10;

  const [formData, setFormData] = useState({
    questionText: '',
    questionType: 'mcq',
    options: [
      { optionText: '', isCorrect: false },
      { optionText: '', isCorrect: false },
      { optionText: '', isCorrect: false },
      { optionText: '', isCorrect: false },
    ],
    correctAnswer: '',
    marks: 1,
    difficulty: 'medium',
  });

  const [continueAdding, setContinueAdding] = useState(false);
  const [questionsAddedCount, setQuestionsAddedCount] = useState(0);
  const [isAddingFromSubject, setIsAddingFromSubject] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchQuestions();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      const coursesData = response.data.data || response.data || [];
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await questionAPI.getAll();
      setQuestions(response.data.data?.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load questions';

      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('You need Super Admin privileges to access this page. Please login as Super Admin.');
        toast.error('Access denied: Super Admin privileges required');
      } else {
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (courseId, isFilter = false) => {
    if (isFilter) {
      setFilterCourse(courseId);
      setFilterSemester('');
      setFilterSubject('');
    } else {
      setSelectedCourse(courseId);
      setSelectedSemester('');
      setSelectedSubject('');
      setAvailableSemesters([]);
      setAvailableSubjects([]);

      if (courseId) {
        const course = courses.find((c) => c._id === courseId);
        if (course && course.semesters) {
          setAvailableSemesters(course.semesters);
        }
      }
    }
  };

  const handleSemesterChange = (semesterId, isFilter = false) => {
    if (isFilter) {
      setFilterSemester(semesterId);
      setFilterSubject('');
    } else {
      setSelectedSemester(semesterId);
      setSelectedSubject('');
      setAvailableSubjects([]);

      if (semesterId && selectedCourse) {
        const course = courses.find((c) => c._id === selectedCourse);
        const semester = course?.semesters?.find((s) => s._id === semesterId);
        if (semester && semester.subjects) {
          setAvailableSubjects(semester.subjects);
        }
      }
    }
  };

  const handleOpenDialog = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setSelectedCourse(question.course._id || question.course);

      // Load semesters for the course
      const course = courses.find((c) => c._id === (question.course._id || question.course));
      if (course && course.semesters) {
        setAvailableSemesters(course.semesters);
        setSelectedSemester(question.semester.semesterId);

        // Load subjects for the semester
        const semester = course.semesters.find((s) => s._id === question.semester.semesterId);
        if (semester && semester.subjects) {
          setAvailableSubjects(semester.subjects);
          setSelectedSubject(question.subject.subjectId);
        }
      }

      setFormData({
        questionText: question.questionText,
        questionType: question.questionType,
        options: question.options || [],
        correctAnswer: question.correctAnswer,
        marks: question.marks,
        difficulty: question.difficulty,
      });
    } else {
      setEditingQuestion(null);
      setSelectedCourse('');
      setSelectedSemester('');
      setSelectedSubject('');
      setAvailableSemesters([]);
      setAvailableSubjects([]);
      setQuestionsAddedCount(0); // Reset counter when opening new dialog
      setFormData({
        questionText: '',
        questionType: 'mcq',
        options: [
          { optionText: '', isCorrect: false },
          { optionText: '', isCorrect: false },
          { optionText: '', isCorrect: false },
          { optionText: '', isCorrect: false },
        ],
        correctAnswer: '',
        marks: 1,
        difficulty: 'medium',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingQuestion(null);
    setQuestionsAddedCount(0); // Reset counter when closing
    setIsAddingFromSubject(false);
    // Reset selections
    setSelectedCourse('');
    setSelectedSemester('');
    setSelectedSubject('');
    setAvailableSemesters([]);
    setAvailableSubjects([]);
  };

  const handleAddQuestionToSubject = (courseId, semesterId, subjectId) => {
    setEditingQuestion(null);
    setQuestionsAddedCount(0);
    setIsAddingFromSubject(true);

    const course = courses.find((c) => c._id === courseId);
    if (course && course.semesters) {
      const semester = course.semesters.find((s) => s._id === semesterId);

      setSelectedCourse(courseId);
      setAvailableSemesters(course.semesters);
      setSelectedSemester(semesterId);

      if (semester && semester.subjects) {
        setAvailableSubjects(semester.subjects);
        setSelectedSubject(subjectId);
      }
    }

    setFormData({
      questionText: '',
      questionType: 'mcq',
      options: [
        { optionText: '', isCorrect: false },
        { optionText: '', isCorrect: false },
        { optionText: '', isCorrect: false },
        { optionText: '', isCorrect: false },
      ],
      correctAnswer: '',
      marks: 1,
      difficulty: 'medium',
    });

    setTimeout(() => {
      setOpenDialog(true);
    }, 100);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index].optionText = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleCorrectAnswerChange = (index) => {
    const newOptions = formData.options.map((opt, i) => ({
      ...opt,
      isCorrect: i === index,
    }));
    setFormData({
      ...formData,
      options: newOptions,
      correctAnswer: newOptions[index].optionText,
    });
  };

  const handleSubmit = async (e, shouldContinue = false) => {
    e.preventDefault();

    if (!selectedCourse || !selectedSemester || !selectedSubject) {
      toast.error('Please select Course, Semester, and Subject');
      return;
    }

    if (!formData.questionText.trim()) {
      toast.error('Please enter the question text');
      return;
    }

    // Prepare payload based on question type
    let finalPayload = {
      course: selectedCourse,
      semester: {
        semesterId: availableSemesters.find((s) => s._id === selectedSemester)._id,
        semesterNumber: availableSemesters.find((s) => s._id === selectedSemester).semesterNumber,
        semesterName: availableSemesters.find((s) => s._id === selectedSemester).name,
      },
      subject: {
        subjectId: availableSubjects.find((s) => s._id === selectedSubject)._id,
        name: availableSubjects.find((s) => s._id === selectedSubject).name,
        code: availableSubjects.find((s) => s._id === selectedSubject).code,
      },
      questionText: formData.questionText,
      questionType: formData.questionType,
      marks: formData.marks,
      difficulty: formData.difficulty,
    };

    // Handle MCQ
    if (formData.questionType === 'mcq') {
      const hasCorrectAnswer = formData.options.some((opt) => opt.isCorrect);
      if (!hasCorrectAnswer) {
        toast.error('Please mark at least one option as correct answer');
        return;
      }

      const hasEmptyOptions = formData.options.some((opt) => !opt.optionText.trim());
      if (hasEmptyOptions) {
        toast.error('Please fill in all option fields');
        return;
      }

      finalPayload.options = formData.options;
      finalPayload.correctAnswer = formData.options.find((opt) => opt.isCorrect)?.optionText || '';
    }
    // Handle True/False
    else if (formData.questionType === 'true-false') {
      if (!formData.correctAnswer) {
        toast.error('Please select True or False as the correct answer');
        return;
      }

      finalPayload.options = [
        { optionText: 'True', isCorrect: formData.correctAnswer === 'True' },
        { optionText: 'False', isCorrect: formData.correctAnswer === 'False' },
      ];
      finalPayload.correctAnswer = formData.correctAnswer;
    }

    try {
      if (editingQuestion) {
        await questionAPI.update(editingQuestion._id, finalPayload);
        toast.success('Question updated successfully');
        handleCloseDialog();
      } else {
        await questionAPI.create(finalPayload);
        const newCount = questionsAddedCount + 1;
        setQuestionsAddedCount(newCount);
        toast.success(`Question ${newCount} created successfully!`);

        if (shouldContinue) {
          // Reset form but keep course/semester/subject selected
          setFormData({
            questionText: '',
            questionType: 'mcq',
            options: [
              { optionText: '', isCorrect: false },
              { optionText: '', isCorrect: false },
              { optionText: '', isCorrect: false },
              { optionText: '', isCorrect: false },
            ],
            correctAnswer: '',
            marks: 1,
            difficulty: 'medium',
          });
        } else {
          handleCloseDialog();
        }
      }

      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to save question';
      const errors = error.response?.data?.errors;

      if (errors && Array.isArray(errors)) {
        console.error('Validation errors:', errors);
        toast.error(`${errorMsg}: ${errors.join(', ')}`);
      } else {
        toast.error(errorMsg);
      }

      console.error('Full error details:', error.response?.data);
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Question',
      message: 'Are you sure you want to delete this question?',
      onConfirm: async () => {
        try {
          await questionAPI.delete(id);
          toast.success('Question deleted successfully');
          fetchQuestions();
        } catch (error) {
          console.error('Error deleting question:', error);
          toast.error('Failed to delete question');
        }
      },
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSubjectPage = (subjectId) => {
    return subjectPages[subjectId] || 1;
  };

  const setSubjectPage = (subjectId, page) => {
    setSubjectPages((prev) => ({ ...prev, [subjectId]: page }));
  };

  const getPaginatedQuestions = (questions, subjectId) => {
    const currentPage = getSubjectPage(subjectId);
    const startIndex = (currentPage - 1) * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    return questions.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalQuestions) => {
    return Math.ceil(totalQuestions / questionsPerPage);
  };

  const filteredQuestions = questions.filter((q) => {
    if (filterCourse && q.course?._id !== filterCourse) return false;
    if (filterSemester && q.semester?.semesterId !== filterSemester) return false;
    if (filterSubject && q.subject?.subjectId !== filterSubject) return false;
    return true;
  });

  const filterSemesters = filterCourse
    ? courses.find((c) => c._id === filterCourse)?.semesters || []
    : [];

  const filterSubjects = filterSemester
    ? filterSemesters.find((s) => s._id === filterSemester)?.subjects || []
    : [];

  // Group questions by semester and subject
  const groupedQuestions = filteredQuestions.reduce((acc, question) => {
    // Skip questions without proper semester/subject structure
    if (!question.semester || !question.semester.semesterId || !question.subject || !question.subject.subjectId) {
      console.warn('Question missing semester or subject data:', question);
      return acc;
    }

    const semesterKey = question.semester.semesterId;
    const subjectKey = question.subject.subjectId;

    if (!acc[semesterKey]) {
      acc[semesterKey] = {
        courseId: question.course?._id || question.course, // Add courseId
        semesterId: question.semester.semesterId,
        semesterName: question.semester.semesterName,
        semesterNumber: question.semester.semesterNumber,
        subjects: {},
      };
    }

    if (!acc[semesterKey].subjects[subjectKey]) {
      acc[semesterKey].subjects[subjectKey] = {
        subjectId: question.subject.subjectId,
        subjectName: question.subject.name,
        subjectCode: question.subject.code,
        questions: [],
      };
    }

    acc[semesterKey].subjects[subjectKey].questions.push(question);
    return acc;
  }, {});

  const semesters = Object.values(groupedQuestions).sort(
    (a, b) => (a.semesterNumber || 0) - (b.semesterNumber || 0)
  );

  // Count questions with missing data
  const questionsWithMissingData = filteredQuestions.filter(
    (q) => !q.semester || !q.semester.semesterId || !q.subject || !q.subject.subjectId
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Question Bank
          </Typography>
        </Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Access Denied</Typography>
          <Typography variant="body1">{error}</Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            The Question Bank feature requires Super Admin privileges.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Question Bank
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Question
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FilterList />
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Course</InputLabel>
            <Select
              value={filterCourse}
              onChange={(e) => handleCourseChange(e.target.value, true)}
              label="Filter by Course"
            >
              <MenuItem value="">All Courses</MenuItem>
              {courses.map((course) => (
                <MenuItem key={course._id} value={course._id}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {filterCourse && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Filter by Semester</InputLabel>
              <Select
                value={filterSemester}
                onChange={(e) => handleSemesterChange(e.target.value, true)}
                label="Filter by Semester"
              >
                <MenuItem value="">All Semesters</MenuItem>
                {filterSemesters.map((sem) => (
                  <MenuItem key={sem._id} value={sem._id}>
                    {sem.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {filterSemester && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Filter by Subject</InputLabel>
              <Select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                label="Filter by Subject"
              >
                <MenuItem value="">All Subjects</MenuItem>
                {filterSubjects.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.name} ({sub.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(filterCourse || filterSemester || filterSubject) && (
            <Button
              size="small"
              onClick={() => {
                setFilterCourse('');
                setFilterSemester('');
                setFilterSubject('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Warning for questions with missing data */}
      {questionsWithMissingData.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {questionsWithMissingData.length} question{questionsWithMissingData.length > 1 ? 's have' : ' has'} incomplete
          semester or subject data and cannot be displayed. Please edit{' '}
          {questionsWithMissingData.length > 1 ? 'these questions' : 'this question'} to assign proper course, semester,
          and subject.
        </Alert>
      )}

      {filteredQuestions.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info">
            No questions found. Click "Add Question" to create your first question.
          </Alert>
        </Paper>
      ) : (
        <Box>
          {semesters.map((semester) => (
            <Accordion key={semester.semesterId} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {semester.semesterName}
                  </Typography>
                  <Chip
                    label={`${Object.values(semester.subjects).reduce((sum, subject) => sum + subject.questions.length, 0)} Questions`}
                    size="small"
                    color="primary"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {Object.values(semester.subjects).map((subject) => (
                    <Accordion key={subject.subjectId} elevation={3}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {subject.subjectName}
                            </Typography>
                            <Chip label={subject.subjectCode} size="small" variant="outlined" />
                            <Chip
                              label={`${subject.questions.length} Question${subject.questions.length !== 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddQuestionToSubject(semester.courseId, semester.semesterId, subject.subjectId);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <Add />
                          </IconButton>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          {getPaginatedQuestions(subject.questions, subject.subjectId).map((question, qIndex) => {
                            const actualIndex = (getSubjectPage(subject.subjectId) - 1) * questionsPerPage + qIndex;
                            return (
                              <Card key={question._id} elevation={2}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                                        <Chip label={`Q${actualIndex + 1}`} size="small" color="primary" />
                                        <Chip
                                          label={question.difficulty || 'Medium'}
                                          size="small"
                                          color={getDifficultyColor(question.difficulty)}
                                          variant="outlined"
                                        />
                                        <Chip
                                          label={`${question.marks || 1} mark${question.marks > 1 ? 's' : ''}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Stack>
                                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                                        {question.questionText}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleOpenDialog(question)}
                                        sx={{ mr: 1 }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                      <IconButton color="error" size="small" onClick={() => handleDelete(question._id)}>
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  <Divider sx={{ my: 2 }} />

                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Options:
                                  </Typography>
                                  <Grid container spacing={1}>
                                    {question.options &&
                                      question.options.map((option, optIndex) => (
                                        <Grid item xs={12} sm={6} key={optIndex}>
                                          <Paper
                                            sx={{
                                              p: 1.5,
                                              backgroundColor: option.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                                              border: (theme) =>
                                                option.isCorrect
                                                  ? '2px solid ' + theme.palette.success.main
                                                  : '1px solid ' + theme.palette.divider,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                            }}
                                          >
                                            {option.isCorrect && <CheckCircle color="success" fontSize="small" />}
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: option.isCorrect ? 600 : 400, flex: 1 }}
                                            >
                                              {String.fromCharCode(65 + optIndex)}. {option.optionText}
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      ))}
                                  </Grid>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>

                        {/* Pagination Controls */}
                        {subject.questions.length > questionsPerPage && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Showing {((getSubjectPage(subject.subjectId) - 1) * questionsPerPage) + 1} -{' '}
                              {Math.min(getSubjectPage(subject.subjectId) * questionsPerPage, subject.questions.length)} of{' '}
                              {subject.questions.length} questions
                            </Typography>
                            <Pagination
                              count={getTotalPages(subject.questions.length)}
                              page={getSubjectPage(subject.subjectId)}
                              onChange={(e, page) => setSubjectPage(subject.subjectId, page)}
                              color="primary"
                              size="medium"
                              showFirstButton
                              showLastButton
                            />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingQuestion ? 'Edit Question' : 'Add New Question'}
            {!editingQuestion && questionsAddedCount > 0 && (
              <Chip
                label={`${questionsAddedCount} question${questionsAddedCount > 1 ? 's' : ''} added in this session`}
                color="success"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Course</InputLabel>
                <Select
                  value={selectedCourse || ''}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  label="Course"
                  disabled={isAddingFromSubject}
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  {courses.map((course) => (
                    <MenuItem key={course._id} value={course._id}>{course.name} ({course.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required disabled={!selectedCourse || isAddingFromSubject}>
                <InputLabel>Semester</InputLabel>
                <Select
                  value={selectedSemester || ''}
                  onChange={(e) => handleSemesterChange(e.target.value)}
                  label="Semester"
                >
                  {availableSemesters.map((sem) => (
                    <MenuItem key={sem._id} value={sem._id}>{sem.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required disabled={!selectedSemester || isAddingFromSubject}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={selectedSubject || ''}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label="Subject"
                >
                  {availableSubjects.map((sub) => (
                    <MenuItem key={sub._id} value={sub._id}>{sub.name} ({sub.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {availableSemesters.length === 0 && selectedCourse && (
                <Alert severity="warning">No semesters found. Please add semesters in Course Management first.</Alert>
              )}

              {availableSubjects.length === 0 && selectedSemester && (
                <Alert severity="warning">No subjects found. Please add subjects in Course Management first.</Alert>
              )}

              <TextField label="Question Text" value={formData.questionText} onChange={(e) => setFormData({ ...formData, questionText: e.target.value })} required fullWidth multiline rows={2} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.questionType}
                      onChange={(e) => {
                        const newType = e.target.value;
                        if (newType === 'true-false') {
                          setFormData({
                            ...formData,
                            questionType: newType,
                            correctAnswer: '',
                            options: [],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            questionType: newType,
                            correctAnswer: '',
                            options: [
                              { optionText: '', isCorrect: false },
                              { optionText: '', isCorrect: false },
                              { optionText: '', isCorrect: false },
                              { optionText: '', isCorrect: false },
                            ],
                          });
                        }
                      }}
                      label="Type"
                    >
                      <MenuItem value="mcq">Multiple Choice (4 Options)</MenuItem>
                      <MenuItem value="true-false">True/False</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Difficulty</InputLabel>
                    <Select value={formData.difficulty} onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })} label="Difficulty">
                      <MenuItem value="easy">Easy</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="hard">Hard</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Marks" type="number" value={formData.marks} onChange={(e) => setFormData({ ...formData, marks: parseInt(e.target.value) })} fullWidth inputProps={{ min: 1 }} />
                </Grid>
              </Grid>

              {formData.questionType === 'mcq' && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Options (Click checkmark to mark as correct):</Typography>
                  {formData.options.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        label={'Option ' + String.fromCharCode(65 + index)}
                        value={option.optionText}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        fullWidth
                        required
                      />
                      <IconButton color={option.isCorrect ? 'success' : 'default'} onClick={() => handleCorrectAnswerChange(index)}>
                        <CheckCircle />
                      </IconButton>
                    </Box>
                  ))}
                </>
              )}

              {formData.questionType === 'true-false' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Select the correct answer:
                  </Typography>
                  <FormControl component="fieldset">
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Button
                        variant={formData.correctAnswer === 'True' ? 'contained' : 'outlined'}
                        color={formData.correctAnswer === 'True' ? 'success' : 'primary'}
                        onClick={() => setFormData({ ...formData, correctAnswer: 'True' })}
                        startIcon={formData.correctAnswer === 'True' ? <CheckCircle /> : null}
                        sx={{ minWidth: 120 }}
                      >
                        True
                      </Button>
                      <Button
                        variant={formData.correctAnswer === 'False' ? 'contained' : 'outlined'}
                        color={formData.correctAnswer === 'False' ? 'error' : 'primary'}
                        onClick={() => setFormData({ ...formData, correctAnswer: 'False' })}
                        startIcon={formData.correctAnswer === 'False' ? <CheckCircle /> : null}
                        sx={{ minWidth: 120 }}
                      >
                        False
                      </Button>
                    </Box>
                  </FormControl>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {!editingQuestion && (
              <Button onClick={(e) => handleSubmit(e, true)} variant="outlined" color="primary">
                Save & Add Another
              </Button>
            )}
            <Button type="submit" variant="contained">
              {editingQuestion ? 'Update' : 'Save & Close'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmColor="error"
        confirmText="Delete"
      />
    </Box>
  );
};

export default QuestionList;
